import { Alert, Button, Card, Empty, Space, Table, Tag } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type PackRow = {
  id: string;
  pack_code: string;
  version: string;
  status: string;
};

type Validation = { valid: boolean; blocker?: string; missing: string[] };

export default function PolicyPacksPage() {
  const { t } = useTranslation('education');
  const [validation, setValidation] = useState<Validation | null>(null);
  const query = useQuery({
    queryKey: ['education-policy-packs'],
    queryFn: () => api.get<PackRow[]>('/education/policy-packs'),
  });

  const validate = useMutation({
    mutationFn: (id: string) => api.post<{ validation: Validation }>(`/education/policy-packs/${id}/validate`, {}),
    onSuccess: (result) => setValidation(result.data?.validation ?? null),
  });

  const rows = query.data?.data ?? [];

  return (
    <EducationPageChrome
      title={t('policyTitle')}
      description={t('policyIntro')}
      tags={<Tag>{rows.length} {t('policyTitle')}</Tag>}
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('policyEmpty', { defaultValue: 'No policy packs configured' })}
                />
              ),
            }}
            columns={[
              { title: t('policyCode'), dataIndex: 'pack_code' },
              { title: t('policyVersion'), dataIndex: 'version', width: 120 },
              {
                title: t('policyStatus'),
                dataIndex: 'status',
                width: 140,
                render: (value: string) => (
                  <Tag color={value === 'active' || value === 'published' ? 'green' : 'default'}>{value}</Tag>
                ),
              },
              {
                title: t('validate'),
                width: 140,
                render: (_value, row) => (
                  <Button size="small" onClick={() => validate.mutate(row.id)} loading={validate.isPending}>
                    {t('validate')}
                  </Button>
                ),
              },
            ]}
          />
          {validation ? (
            <Alert
              type={validation.valid ? 'success' : 'warning'}
              showIcon
              message={`${t('validationResult')}: ${validation.valid ? 'valid' : validation.blocker || 'blocked'}`}
              description={validation.missing?.length ? validation.missing.join(', ') : undefined}
            />
          ) : null}
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
